<script lang="ts">
  import { addDays, average7, dateTicks, daysBetween } from '../../utils/chart';
  import { count, today } from '../../utils/insights';
  import { weekStart } from '../../utils/progress';
  import {
    BODY_COLOR, BODY_KINDS, HEART_COLOR, READING_NAMES, SLEEP_COLOR, STEPS_COLOR, VITAL_KINDS, VITALS_COLOR,
    bucketLabel, bucketName, hhmmClock, readingNumber, readingUnit, readingValue, shownValue, weightChange, short, sleepTime,
    type HealthPeriod, type ReadingCard, type ReadingKind,
  } from '../../utils/health';
  import { decimal, integer, locale, month, monthDay, t } from '../../i18n/index.ts';
  import HealthCard from './HealthCard.svelte';
  import BarChart from './BarChart.svelte';
  import PointChart from './PointChart.svelte';

  // PeriodView is the Week, Month, or Year tab (spec 007 FR-020): each
  // card's average a day over the period, a bar or point per day, week, or
  // month, and Steps' and Sleep's Typical (007 FR-021). A card without data is
  // left out (007 FR-018). Weight adds its smoothed trend, sleep how much its
  // times vary, and HRV its normal range (spec 007 FR-103, 007 FR-104, 007 FR-105).
  // As every chart does (spec 010 FR-033), resting heart rate and HRV,
  // taken once a day, are faint daily points under their 7-day average,
  // read from the period's daily values, which start 6 days before it, and
  // weight's readings are faint under its trend.
  let { p }: { p: HealthPeriod } = $props();

  const REST_COLOR = 'var(--theme-red)';
  const ws = weekStart(navigator.language);

  let daily = $derived(p.daily ?? []);

  let periodDays = $derived(Array.from({ length: daysBetween(p.from, p.to) + 1 }, (_, i) => addDays(p.from, i)));
  let dayNames = $derived.by(() => {
    const f = new Intl.DateTimeFormat(locale(), { weekday: 'long', month: 'long', day: 'numeric' });
    return periodDays.map((d) => f.format(new Date(`${d}T12:00:00`)));
  });
  // dayMarks are the daily charts' marks: each weekday for a week, round
  // days or months for longer.
  let dayMarks = $derived.by(() => {
    if (p.bucket === 'day') return { labels, ticks: undefined };
    const n = periodDays.length;
    const marks = dateTicks(p.from, p.to, 7, ws);
    return {
      labels: undefined,
      ticks: marks.days.map((d) => {
        const at = new Date(`${d}T12:00:00`);
        return { at: (daysBetween(p.from, d) + 0.5) / n, text: marks.unit === 'month' ? month(at, true) : monthDay(at) };
      }),
    };
  });

  // dailyOf is a once-a-day measure over the period's days with its 7-day
  // average, null without any value.
  function dailyOf(f: (d: HealthPeriod['daily'][number]) => number | undefined): { values: (number | null)[]; avg: (number | null)[] } | null {
    const byDay = new Map(daily.map((d) => [d.date, f(d) ?? null]));
    const values = periodDays.map((d) => byDay.get(d) ?? null);
    if (!values.some((v) => v != null)) return null;
    const lead = Array.from({ length: 6 }, (_, i) => byDay.get(addDays(p.from, i - 6)) ?? null);
    const now = today();
    const avg = average7([...lead, ...values]).slice(6).map((a, i) => (periodDays[i] <= now ? a : null));
    return { values, avg };
  }
  let restDaily = $derived(dailyOf((d) => d.resting_hr));
  let hrvDaily = $derived(dailyOf((d) => d.hrv));

  let labels = $derived(p.buckets.map((b) => bucketLabel(p.bucket, b.from, b.to)));
  let names = $derived(p.buckets.map((b) => bucketName(p.bucket, b.from, b.to)));
  let slot = $derived(t('healthBucketColumn', { bucket: p.bucket }));
  let dayColumn = $derived(t('healthBucketColumn', { bucket: 'day' }));
  const bpm = (v: number) => t('healthBpm', { bpm: integer(Math.round(v)) });

  // pointsOf lines a reading's per-bucket averages up with the period's buckets.
  function pointsOf(c: ReadingCard, second = false): (number | null)[] {
    return p.buckets.map((b) => {
      const pt = c.buckets.find((x) => x.from === b.from);
      if (!pt) return null;
      return second ? (pt.value2 ?? null) : readingNumber(c.kind, pt.value);
    });
  }

  // readingFormat writes a charted value in the viewer's unit, which
  // pointsOf already converted to.
  function readingFormat(kind: ReadingKind): (v: number) => string {
    return (v: number) => shownValue(kind, v);
  }

  // seriesOf is a card's lines: its readings, faint under weight's trend.
  function seriesOf(c: ReadingCard): { name: string; color: string; values: (number | null)[]; kind?: 'line' | 'faint' | 'smooth' }[] {
    if (c.kind === 'blood_pressure') {
      return [{ name: t('healthSystolic'), color: 'var(--theme-red)', values: pointsOf(c) }, { name: t('healthDiastolic'), color: 'var(--theme-sapphire)', values: pointsOf(c, true) }];
    }
    if (!c.trend?.length) return [{ name: READING_NAMES[c.kind], color: 'var(--theme-text)', values: pointsOf(c) }];
    return [
      { name: READING_NAMES[c.kind], color: 'var(--theme-text)', values: pointsOf(c), kind: 'faint' },
      {
        name: t('healthTrendLine'),
        color: BODY_COLOR,
        values: p.buckets.map((b) => { const pt = c.trend!.find((x) => x.from === b.from); return pt ? readingNumber(c.kind, pt.value) : null; }),
        kind: 'smooth',
      },
    ];
  }

  // readingCaption describes a reading card's chart for a screen reader.
  function readingCaption(c: ReadingCard): string {
    const extra = c.trend?.length ? (c.range ? 'both' : 'trend') : c.range ? 'range' : 'none';
    return t('healthReadingCaption', { reading: READING_NAMES[c.kind], bucket: p.bucket, extra });
  }

  // trendText is "Trend −0.8 kg this month", in pounds under stones, and
  // "over the month" for a past period.
  function trendText(c: ReadingCard): string {
    const { value: v, unit } = weightChange(c.trend_change!);
    const tenths = Math.round(Math.abs(v) * 10) / 10;
    const sign = tenths === 0 ? '' : v < 0 ? '−' : '+';
    const when = `${p.to >= today() ? 'this' : 'over'}_${p.period}`;
    return t('healthTrend', { change: `${sign}${decimal(tenths, 1)}`, unit, when });
  }

  // spread is "±25 min".
  const spread = (m: number) => t('healthSpread', { minutes: integer(Math.round(m)) });

  const cardsOf = (kinds: ReadingKind[]) => p.readings.filter((r) => kinds.includes(r.kind));
  let body = $derived(cardsOf(BODY_KINDS));
  let vitals = $derived(cardsOf(VITAL_KINDS));
  let empty = $derived(!p.steps && !p.sleep && !p.heart && p.readings.length === 0);
</script>

{#snippet readingCharts(cards: ReadingCard[])}
  <div class="grid gap-x-6 gap-y-4 md:grid-cols-2">
    {#each cards as c (c.kind)}
      <div>
        <p class="flex items-baseline justify-between gap-2 text-sm mb-1">
          <span>{READING_NAMES[c.kind]}</span>
          <span><span class="text-xs text-text-muted mr-1">{t('healthAverage')}</span><span class="font-semibold">{readingValue(c.kind, c.value, c.value2)}</span></span>
        </p>
        {#if c.kind === 'hrv' && hrvDaily}
          <PointChart
            series={[
              { name: t('healthEachDay'), color: VITALS_COLOR, values: hrvDaily.values, kind: 'faint' },
              { name: t('healthSevenDayAverage'), color: VITALS_COLOR, values: hrvDaily.avg, kind: 'smooth' },
            ]}
            band={c.range ? periodDays.map(() => ({ lo: c.range!.low, hi: c.range!.high })) : undefined} bandName={t('healthNormalRange')}
            labels={dayMarks.labels} ticks={dayMarks.ticks} names={dayNames} column={dayColumn} unit={readingUnit(c.kind)}
            caption={t('healthHrvCaption', { range: c.range ? 'yes' : 'no' })} format={readingFormat(c.kind)} short />
        {:else}
          <PointChart series={seriesOf(c)}
            band={c.range ? p.buckets.map(() => ({ lo: c.range!.low, hi: c.range!.high })) : undefined} bandName={t('healthNormalRange')} unit={readingUnit(c.kind)}
            {labels} {names} column={slot} caption={readingCaption(c)} format={readingFormat(c.kind)} short />
        {/if}
        {#if c.trend_change != null}
          <p class="text-xs text-text-muted mt-1">{trendText(c)}</p>
        {/if}
        {#if c.range}
          <p class="text-xs text-text-muted mt-1">{t('healthRangeLatest', {
            value: readingValue(c.kind, c.range.latest), vs: c.range.latest_vs, low: integer(Math.round(c.range.low)), high: integer(Math.round(c.range.high)), unit: readingUnit(c.kind),
          })}</p>
        {/if}
      </div>
    {/each}
  </div>
{/snippet}

{#if empty}
  <p class="text-sm text-text-muted">{t('healthNothingPeriod', { period: p.period })}</p>
{:else}
  <div class="grid gap-4 md:grid-cols-2 items-start">
    {#if p.steps}
      <HealthCard title={t('healthSteps')} icon="la-shoe-prints" color={STEPS_COLOR} label={t('healthAverage')} value={count(p.steps.value)}
        typical={p.steps.typical != null ? short(p.steps.typical) : undefined}>
        <BarChart values={p.buckets.map((b) => b.steps)} color={STEPS_COLOR} typical={p.steps.typical} {labels} {names}
          caption={t('healthStepsCaption', { bucket: p.bucket, typical: p.steps.typical != null ? 'yes' : 'no' })} format={count} unit={t('healthUnitSteps')} tick={short}
          heading={{ name: slot, value: t('healthStepsADay') }} />
      </HealthCard>
    {/if}

    {#if p.sleep}
      <HealthCard title={t('healthSleep')} icon="la-bed" color={SLEEP_COLOR} label={t('healthAverage')} value={sleepTime(p.sleep.asleep_s.value)}
        typical={p.sleep.asleep_s.typical != null ? sleepTime(p.sleep.asleep_s.typical) : undefined}
        side={{ label: t('healthDeepSleep'), value: sleepTime(p.sleep.deep_s) }}
        details={[
          { label: t('healthAvgDeep'), value: sleepTime(p.sleep.deep_s) },
          { label: t('healthAvgFallAsleep'), value: p.sleep.fall_asleep ? hhmmClock(p.sleep.fall_asleep) : '–' },
          { label: t('healthAvgWakeUp'), value: p.sleep.wake_up ? hhmmClock(p.sleep.wake_up) : '–' },
        ]}>
        <BarChart values={p.buckets.map((b) => b.asleep_s)} inner={p.buckets.map((b) => b.deep_s)} color={SLEEP_COLOR} innerColor={SLEEP_COLOR}
          typical={p.sleep.asleep_s.typical} {labels} {names} format={sleepTime} steps={[3600, 7200, 10800, 21600]} tick={(v) => t('healthHoursTick', { hours: integer(v / 3600) })}
          caption={t('healthSleepCaption', { bucket: p.bucket, typical: p.sleep.asleep_s.typical != null ? 'yes' : 'no' })}
          heading={{ name: slot, value: t('healthAsleep'), inner: t('healthDeepSleep') }} />
        {#if p.sleep.fall_asleep_spread_min != null && p.sleep.wake_up_spread_min != null}
          <p class="text-xs text-text-muted mt-2">{t('healthSleepSpread', { fall: spread(p.sleep.fall_asleep_spread_min), wake: spread(p.sleep.wake_up_spread_min) })}</p>
        {/if}
      </HealthCard>
    {/if}

    {#if p.heart}
      <HealthCard wide title={t('healthHeartRate')} icon="la-heartbeat" color={HEART_COLOR} label={t('healthAverage')}
        value={p.heart.avg_hr != null ? integer(Math.round(p.heart.avg_hr)) : undefined} unit={t('healthUnitBpm')}
        side={p.heart.resting_hr != null ? { label: t('healthResting'), value: integer(Math.round(p.heart.resting_hr)) } : undefined}>
        <PointChart
          series={[
            { name: t('healthAverage'), color: HEART_COLOR, values: p.buckets.map((b) => b.avg_hr) },
            ...(restDaily ? [] : [{ name: t('healthResting'), color: REST_COLOR, values: p.buckets.map((b) => b.resting_hr) }]),
          ]}
          {labels} {names} column={slot} caption={t('healthHeartCaption', { bucket: p.bucket, resting: restDaily ? 'no' : 'yes' })} format={bpm} unit={t('healthUnitBpm')} />
        {#if restDaily}
          <p class="text-sm mt-4 mb-1">{t('healthRestingHeartRate')}</p>
          <PointChart
            series={[
              { name: t('healthEachDay'), color: REST_COLOR, values: restDaily.values, kind: 'faint' },
              { name: t('healthSevenDayAverage'), color: REST_COLOR, values: restDaily.avg, kind: 'smooth' },
            ]}
            labels={dayMarks.labels} ticks={dayMarks.ticks} names={dayNames} column={dayColumn} caption={t('healthRestingCaption')} format={bpm} unit={t('healthUnitBpm')} short />
        {/if}
      </HealthCard>
    {/if}

    {#if body.length}
      <HealthCard wide title={t('healthBody')} icon="la-weight" color={BODY_COLOR}>
        {@render readingCharts(body)}
      </HealthCard>
    {/if}

    {#if vitals.length}
      <HealthCard wide title={t('healthVitals')} icon="la-stethoscope" color={VITALS_COLOR}>
        {@render readingCharts(vitals)}
      </HealthCard>
    {/if}
  </div>
{/if}
